import { useRef, useState } from "react";
import styled from "styled-components";
import PanelHeader from "../../components/molecules/PanelHeader";
import Tabs from "../../components/molecules/Tabs";
import colours from "../../lib/colours";
import { nvrTools } from "../../lib/types";
import { stageNames } from "../../lib/workflow";
import DrawWidget from "../Other/DrawWidget";
import FeaturesList from "../Other/FeaturesList";
import UploadWidget from "../Other/UploadWidget";
import { ReactComponent as ArrowRightIcon } from "../../assets/svg/arrow-right.svg";
import SpinnerBase from "../../components/atoms/SpinnerBase";
import useExportFeatures from "../../utils/useExportFeatures";

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: row;
  height: 56px;
  border-top: 1px solid ${colours.lightGrey};
`;

const FooterButton = styled.button`
  width: 50%;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  padding: 8px 16px;
  color: ${colours.th1};
  border: none;
  transition: background-color 0.3s linear;
  &:hover {
    background-color: ${colours.th2d1};
  }
`;

const PrimaryFooterButton = styled(FooterButton)`
  background-color: ${colours.th2};
  color: ${colours.th1};
  &:hover {
    background-color: ${colours.th2d1};
  }
`;

const SecondaryFooterButton = styled(FooterButton)`
  background-color: ${colours.white};
  &:hover {
    background-color: ${colours.th1l5};
  }
  &:disabled {
    background-color: ${colours.offWhite};
    color: ${colours.th1l2};
    cursor: not-allowed;
    &:hover {
      background-color: ${colours.offWhite};
    }
  }
`;

const FooterArrowIcon = styled(ArrowRightIcon)`
  height: 14px;
  margin-left: 10px;
`;

const spinnerStyle = {
  display: "flex",
  alignItems: "center",
  marginLeft: "6px",
  width: "auto",
};

const spinnerChildrenStyle = {
  width: "8px",
  height: "8px",
  backgroundColor: colours.th1l2,
};

interface IProps {
  mode: nvrTools;
  close: any;
  setStage: any;
  showFooter: boolean;
}

function MapNativeVeg({ mode, close, setStage, showFooter }: IProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [exportingShapefile, setExportingShapefile] = useState(false);

  const exportToShapefile = useExportFeatures({
    setExporting: setExportingShapefile,
    setExportError: () => {},
  });

  const drawnGraphicRef = useRef<any>();
  const nextStage = mode === nvrTools.removal ? 2 : 1;

  const onSummaryClick = () => {
    setStage(nextStage);
  };

  return (
    <Container>
      <PanelHeader title={stageNames["mapNativeVeg"]} onClose={close} />
      <Tabs
        names={["draw", "upload"]}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      {activeTab === 0 ? (
        <DrawWidget drawnGraphicRef={drawnGraphicRef} />
      ) : (
        <UploadWidget />
      )}
      <FeaturesList drawnGraphicRef={drawnGraphicRef} />
      {showFooter ? (
        <Footer>
          <SecondaryFooterButton
            onClick={exportToShapefile}
            disabled={exportingShapefile}
          >
            <span>{exportingShapefile ? "Exporting" : "Export Features"}</span>
            {exportingShapefile && (
              <SpinnerBase
                style={spinnerStyle}
                childrenStyle={spinnerChildrenStyle}
              />
            )}
          </SecondaryFooterButton>
          <PrimaryFooterButton onClick={onSummaryClick}>
            <span>To Summary</span>
            <FooterArrowIcon />
          </PrimaryFooterButton>
        </Footer>
      ) : null}
    </Container>
  );
}

export default MapNativeVeg;
